import React, { useState } from "react";
import {
  Table,
  NonCancelableCustomEvent,
  Pagination,
  Container,
  Header,
  Box,
} from "@amzn/awsui-components-react-v3";
import {
  getUrl,
  PersonPopover,
  useLocalStorageState,
} from "@amzn/squad-front-end-shared-react-components";
import InfoLink from "../common/infoLink/InfoLink";
import permissionsPage from "./permissions_page.css";
import { InheritedAccessDetails } from "../createTeam/inheritedAccessDetails";
import AddUser from "./addUser/AddUser";
import { UserAccess } from "./UserAccess";
import { DisplayHelpPanel } from "../help_panel/helpPanelSlice";
import { TeamDtoWithBreadcrumb } from "../layout/subheader/SubHeaderDto";
import { MetricsState } from "../logging";
import { LocalStorageStateKeys } from "../common/userPreferences/LocalStorageStateKeys";
import styles from "../common/person-popover.module.css";

export interface TableColumnSort {
  column: string;
  descending: boolean;
}

export interface PermissionsTableProps {
  inheritedAccessDetails: InheritedAccessDetails[];
  team: TeamDtoWithBreadcrumb;
  metrics: MetricsState;
  setHelpPanelContent: (payload: DisplayHelpPanel | undefined) => void;
  setHelpPanelOpenState: (payload: boolean | true) => void;
  reloadPermissionsTable: () => void;
  columnDefinitions: any;
  isAuthorized: boolean | undefined;
  isLoading: boolean;
}

/**
 * Here, we create the response object which will be passed to polaris table such that the team owner is always the first row in the table.
 * @param accessList is the list of inherited accessDetails we got from Proxy
 * @param actualTeamId is the teamId of team for which we are getting inherited access details.
 * @param teamOwnerId is the employeeId of the team owner.
 */
const createResponse = (
  accessList: InheritedAccessDetails[],
  actualTeamId: string,
  metrics: MetricsState,
  teamOwnerId?: string
): UserAccess[] => {
  let teamOwner: UserAccess | undefined;
  const response: UserAccess[] = accessList
    .map((eachAccess) => {
      return {
        userEmployeeId: eachAccess.employeeId,
        userFullName: eachAccess.employeeFullName,
        userPopoverComponent: (
          <span>
            <PersonPopover
              key={eachAccess.employeeId}
              position="bottom"
              metrics={metrics}
              triggerType={"custom"}
              popoverTrigger={
                <span className={styles["popover-text-border"]}>
                  {eachAccess.employeeFullName}
                </span>
              }
              rosterBaseUrl={getUrl("roster2", true)}
              employeeData={{
                employeeId: eachAccess.employeeId,
                employeeFullName: eachAccess.employeeFullName,
                employeeLogin: eachAccess.employeeLogin,
                employeeStatus: eachAccess.employeeStatus,
                jobTitleName: eachAccess.jobTitleName,
                costCenterId: eachAccess.costCenterId,
                costCenterName: eachAccess.costCenterName,
                teamId: eachAccess.employeeTeamId,
                teamName: eachAccess.employeeTeamName,
              }}
            />
          </span>
        ),
        userLogin: eachAccess.employeeLogin,
        isTeamOwner:
          eachAccess.teamId === actualTeamId &&
          eachAccess.employeeId === teamOwnerId,
        isInheritedAccess: eachAccess.teamId !== actualTeamId,
        teamName: eachAccess.teamName,
        sortAccessType: `${eachAccess.teamId !== actualTeamId}_${
          eachAccess.teamName
        }`,
        // Just in case 2 admins have the same full name, we use combination of fullName and employeeLogin for sorting in polaris table.
        sortName: `${eachAccess.employeeFullName}_${eachAccess.employeeLogin}`,
      };
    })
    .filter((item) => {
      if (item.isTeamOwner) {
        teamOwner = item;
      }
      return !item.isTeamOwner;
    });
  return teamOwner ? [teamOwner, ...response] : response;
};

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  inheritedAccessDetails,
  team,
  metrics,
  reloadPermissionsTable,
  setHelpPanelContent,
  setHelpPanelOpenState,
  columnDefinitions,
  isAuthorized,
  isLoading,
}): JSX.Element => {
  const actualTeamId = team.teamId;
  const teamOwnerId = team.owner ? team.owner.employeeId : undefined;

  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize] = useState(10);

  // sorting handling for the table
  const [sortColumn, setSortColumn] = useLocalStorageState<TableColumnSort>(
    LocalStorageStateKeys.PERMISSIONS_TABLE_SORT_COLUMN,
    {
      column: "sortName",
      descending: false,
    }
  );
  const handleSortChange = (
    event: NonCancelableCustomEvent<{
      sortingColumn: { sortingField?: string };
      sortingDescending?: boolean;
    }>
  ): void => {
    const newColumn = event.detail.sortingColumn.sortingField || "";
    const isDescending = event.detail.sortingDescending ?? false;
    if (sortColumn.column === newColumn) {
      setSortColumn({
        column: newColumn,
        descending: !sortColumn.descending,
      });
    } else {
      setSortColumn({
        column: newColumn,
        descending: isDescending,
      });
    }
  };

  const getSortedItems = (items: UserAccess[]): UserAccess[] => {
    if (!sortColumn.column) return items;

    return [...items].sort((a, b) => {
      if (a.isTeamOwner && !b.isTeamOwner) return -1;
      if (!a.isTeamOwner && b.isTeamOwner) return 1;

      let comparison = 0;

      switch (sortColumn.column) {
        case "sortName":
          comparison = (a.sortName || "").localeCompare(b.sortName || "");
          break;
        case "sortAccessType":
          comparison = (a.sortAccessType || "").localeCompare(
            b.sortAccessType || ""
          );
          break;
        default:
          return 0;
      }

      return sortColumn.descending ? -comparison : comparison;
    });
  };

  const allItems = getSortedItems(
    createResponse(inheritedAccessDetails, actualTeamId, metrics, teamOwnerId)
  );

  const startIndex = (currentPageIndex - 1) * pageSize;
  const paginatedItems = allItems.slice(startIndex, startIndex + pageSize);

  return (
    <Container disableContentPaddings>
      <Table<UserAccess>
        className={permissionsPage["permissions-table"]}
        columnDefinitions={columnDefinitions.map((col: any) => {
          const sortingFieldMap: { [key: string]: string } = {
            name: "sortName",
            accessType: "sortAccessType",
          };
          return {
            ...col,
            sortingField: sortingFieldMap[col.id],
          };
        })}
        wrapLines
        loadingText="Loading permissions"
        loading={isLoading}
        items={paginatedItems}
        sortingColumn={{ sortingField: sortColumn.column }}
        sortingDescending={sortColumn.descending}
        onSortingChange={handleSortChange}
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={Math.ceil(allItems.length / pageSize)}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        }
        header={
          <Header
            actions={
              <AddUser
                team={team}
                metrics={metrics}
                isLoading={isLoading}
                isAuthorized={isAuthorized}
                reloadPermissionsTable={reloadPermissionsTable}
              />
            }
            counter={`(${inheritedAccessDetails.length})`}
            info={
              <InfoLink
                onClick={() => {
                  setHelpPanelContent({
                    header: "User permissions",
                    contentType: "permissions",
                    widenContent: true,
                  });
                  setHelpPanelOpenState(true);
                }}
              />
            }
          >
            User Permissions
          </Header>
        }
        empty={
          <Box textAlign="center">
            <Box padding={{ top: "xs", bottom: "xs" }}>
              <b>No employees</b>
            </Box>
            <Box padding={{ bottom: "s" }}>No employees to display</Box>
            <Box padding={{ bottom: "l" }} />
          </Box>
        }
      />
    </Container>
  );
};

export default PermissionsTable;
